import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Sparkles, Mountain, Coffee, CloudRain, UtensilsCrossed, Users, Gem } from "lucide-react";
import { ATTRACTIONS, ATTRACTIONS_MARQUEE, CURATED_LISTS } from "@/data/site";
import AttractionsMarquee from "@/components/AttractionsMarquee";
import Lightbox from "@/components/Lightbox";

/**
 * PlaceCard — the actual card content, reused by both the desktop grid and
 * the mobile scroll-snap carousel. Accepts an optional prominent title.
 */
function PlaceCard({ p, index = 0, mobile = false }) {
  return (
    <div
      className={`rounded-2xl bg-white border border-[var(--pam-forest)]/10 p-6 md:p-9 shadow-sm ${
        mobile ? "" : "hover:-translate-y-1 hover:shadow-xl transition-transform duration-300"
      }`}
      data-testid={`attractions-place-${p.place.toLowerCase()}`}
    >
      {/* Poetic hero title */}
      {p.title && (
        <div className="mb-4">
          <span className="hidden-gems-badge" data-testid={`place-badge-${p.place.toLowerCase()}`}>
            Destination {String(index + 1).padStart(2, "0")}
          </span>
          <h3 className="mt-3 font-serif italic text-2xl md:text-4xl text-[var(--pam-forest)] leading-tight">
            {p.title}
          </h3>
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-[var(--pam-gold)]">
            <MapPin className="w-4 h-4" />
            <span className="overline">{p.place}</span>
          </div>
        </div>
      </div>
      <div className="mt-5 divide-y divide-[var(--pam-forest)]/10">
        {p.groups.map((g) => (
          <div key={g.title} className="py-4 first:pt-0 last:pb-0 md:grid md:grid-cols-12 md:gap-6 md:items-start">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[var(--pam-coffee)] md:col-span-3 md:pt-2">
              {g.title}
            </div>
            <ul className="mt-3 md:mt-0 md:col-span-9 flex flex-wrap gap-2">
              {g.items.map((it) => (
                <li
                  key={it}
                  className="text-sm px-3 py-1.5 rounded-full bg-[var(--pam-bg)] border border-[var(--pam-forest)]/10 text-[var(--pam-text)]"
                >
                  {it}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * MalnadAtGlance — poetic content strip that fills the "Visit Places" white
 * space between the section header and the destination cards.
 */
function MalnadAtGlance() {
  const bits = [
    { icon: Mountain, k: "Western Ghats", v: "Karnataka's misty spine" },
    { icon: Coffee, k: "Coffee country", v: "Estates since the 1800s" },
    { icon: CloudRain, k: "Monsoon-fed", v: "Waterfalls in full flow" },
    { icon: UtensilsCrossed, k: "Malnad table", v: "Home-cooked, banana-leaf" },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8 }}
      className="mt-10 md:mt-14 rounded-3xl bg-[var(--pam-forest)] text-white p-6 md:p-10 relative overflow-hidden"
      data-testid="malnad-at-glance"
    >
      <div className="flex flex-col md:grid md:grid-cols-12 gap-8 md:items-center">
        <div className="md:col-span-6 min-w-0">
          <div className="inline-flex items-center gap-2 text-[var(--pam-gold)]">
            <Sparkles className="w-4 h-4" />
            <span className="overline">Malnad at a glance</span>
          </div>
          <h3 className="mt-3 font-serif text-2xl sm:text-3xl md:text-4xl leading-tight">
            Five destinations,
            <span className="italic text-[var(--pam-gold)]"> one storied landscape.</span>
          </h3>
          <p className="mt-5 text-white/75 leading-relaxed max-w-lg">
            The Malnad belt of Karnataka is a lush, monsoon-fed corridor of the
            Western Ghats — coffee & cardamom estates, hidden waterfalls, ancient
            temples, and Hoysala heritage — all within a couple of hours of each
            other. Pick your slow weekend, or let us stitch a full itinerary.
          </p>
        </div>
        <div className="md:col-span-6 min-w-0 w-full">
          {/* Desktop: 2x2 tile grid */}
          <div className="hidden md:grid grid-cols-2 gap-4">
            {bits.map(({ icon: Icon, k, v }) => (
              <div
                key={k}
                className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 md:p-5 backdrop-blur-sm"
                data-testid={`glance-tile-${k.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon className="w-5 h-5 text-[var(--pam-gold)]" />
                <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-[var(--pam-gold)]">
                  {k}
                </div>
                <div className="mt-1 font-serif text-lg leading-tight">{v}</div>
              </div>
            ))}
          </div>
          {/* Mobile: swipeable scroll-snap carousel — tiles sit side-by-side */}
          <div className="md:hidden pam-glance-scroller" data-testid="glance-mobile-carousel">
            {bits.map(({ icon: Icon, k, v }) => (
              <div
                key={k}
                className="pam-glance-slide rounded-2xl bg-white/[0.04] border border-white/10 p-4 backdrop-blur-sm"
                data-testid={`glance-tile-mobile-${k.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon className="w-5 h-5 text-[var(--pam-gold)]" />
                <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-[var(--pam-gold)]">
                  {k}
                </div>
                <div className="mt-1 font-serif text-lg leading-tight">{v}</div>
              </div>
            ))}
          </div>
          <p className="md:hidden mt-3 text-[10px] uppercase tracking-[0.24em] text-white/50 text-center" data-testid="glance-mobile-hint">
            &larr; Swipe to explore &rarr;
          </p>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * MobileDestinationCarousel — CSS scroll-snap carousel used ≤768px.
 * Each place occupies exactly 100vw. Dot pagination reflects the active slide
 * and lets the user jump. All transitions are hardware-accelerated (translate).
 */
function MobileDestinationCarousel({ places }) {
  const scrollerRef = useRef(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const w = el.clientWidth;
      const idx = Math.round(el.scrollLeft / w);
      setActive((prev) => (prev === idx ? prev : idx));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const jumpTo = (i) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: el.clientWidth * i, behavior: "smooth" });
  };

  return (
    <div className="md:hidden" data-testid="mobile-destination-carousel">
      <div ref={scrollerRef} className="pam-dest-scroller">
        {places.map((p, i) => (
          <div
            key={p.place}
            className="pam-dest-slide"
            data-testid={`dest-slide-${p.place.toLowerCase()}`}
          >
            <PlaceCard p={p} index={i} mobile />
          </div>
        ))}
      </div>
      <div className="pam-dest-dots" data-testid="dest-dots">
        {places.map((p, i) => (
          <button
            key={p.place}
            type="button"
            aria-label={`Go to ${p.place}`}
            className={`pam-dest-dot ${i === active ? "is-active" : ""}`}
            onClick={() => jumpTo(i)}
            data-testid={`dest-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * CuratedListCard — a curated best-of card. On mobile, its <ul> is rendered
 * as a vertical auto-scrolling ticker; on desktop it stays a static list.
 * Hidden Gems is visually highlighted via `.hidden-gems-card` accent.
 */
function CuratedListCard({ c, i }) {
  const isHiddenGems = /hidden gems/i.test(c.title);
  const items = [...c.items, ...c.items];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay: i * 0.05 }}
      className={`relative rounded-2xl text-white p-6 border ${
        isHiddenGems
          ? "hidden-gems-card md:col-span-2 lg:col-span-1"
          : "bg-[var(--pam-forest)] border-white/10"
      }`}
      data-testid={`curated-${c.title.split(" ").pop().toLowerCase()}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--pam-gold)]">
          {c.title}
        </div>
        {isHiddenGems && (
          <span className="hidden-gems-badge" data-testid="hidden-gems-badge">
            <Gem className="w-3 h-3" /> Editors&rsquo; Pick
          </span>
        )}
      </div>

      {isHiddenGems && (
        <p className="mt-3 text-sm text-white/80 leading-relaxed">
          Secret vantage points, tiny plantations and forgotten waterfalls only
          the locals talk about. Ask our team on WhatsApp — we&rsquo;ll route you
          right.
        </p>
      )}

      {/* Desktop static list */}
      <ul className="mt-4 space-y-2 text-sm text-white/85 hidden md:block">
        {c.items.map((it) => (
          <li key={it} className="flex items-start gap-2">
            <span className="mt-1.5 w-1 h-1 rounded-full bg-[var(--pam-gold)] shrink-0" />
            {it}
          </li>
        ))}
      </ul>

      {/* Mobile vertical auto-scrolling ticker */}
      <div
        className="pam-ticker-wrap mt-4 md:hidden"
        style={{ height: "168px" }}
        data-testid={`curated-ticker-${c.title.split(" ").pop().toLowerCase()}`}
      >
        <ul className="pam-ticker-track space-y-2 text-sm text-white/85">
          {items.map((it, idx) => (
            <li key={`${it}-${idx}`} className="flex items-start gap-2 py-1">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-[var(--pam-gold)] shrink-0" />
              {it}
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

export default function Attractions() {
  const [lightbox, setLightbox] = useState(null);

  // Reorder curated lists so "Hidden Gems" appears first for prominence
  const orderedCurated = [...CURATED_LISTS].sort((a, b) => {
    const aH = /hidden gems/i.test(a.title) ? -1 : 0;
    const bH = /hidden gems/i.test(b.title) ? -1 : 0;
    return aH - bH;
  });

  return (
    <section
      id="gallery"
      className="relative bg-[var(--pam-bg)]"
      style={{ padding: "5% 0" }}
      data-testid="attractions-section"
    >
      <div className="container-pam">
        <div className="max-w-3xl">
          <p className="overline gold-text">Nearby attractions</p>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[var(--pam-forest)]">
            Peaks, waterfalls & temples
            <span className="italic text-[var(--pam-coffee)]">
              {" "}within a short drive.
            </span>
          </h2>
          <p className="mt-6 text-[var(--pam-muted)] leading-relaxed max-w-2xl">
            Everything the Western Ghats are famous for — curated by region and
            interest. Our team can plan a day, a weekend or a whole week around
            these.
          </p>
        </div>

        {/* Malnad at a glance — fills the previously blank space */}
        <MalnadAtGlance />
      </div>

      {/* Continuous horizontal image marquee */}
      {ATTRACTIONS_MARQUEE && ATTRACTIONS_MARQUEE.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.9 }}
          className="mt-14 md:mt-16"
        >
          <AttractionsMarquee items={ATTRACTIONS_MARQUEE} onOpen={setLightbox} />
          <div className="container-pam mt-3">
            <p className="text-xs text-[var(--pam-muted)]">
              Hover / tap to pause · Click any tile to view full-screen
            </p>
          </div>
        </motion.div>
      )}

      <div className="container-pam">
        {/* Desktop: single-column full-width — each destination fills the row so tags spread wide, no dead space right */}
        <div className="mt-14 hidden md:grid grid-cols-1 gap-8">
          {ATTRACTIONS.map((p, i) => (
            <motion.div
              key={p.place}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
            >
              <PlaceCard p={p} index={i} />
            </motion.div>
          ))}
        </div>

        {/* Mobile: scroll-snap swipeable carousel */}
        <div className="mt-10">
          <MobileDestinationCarousel places={ATTRACTIONS} />
        </div>

        {/* Curated best-of lists */}
        <div className="mt-20">
          <div className="inline-flex items-center gap-2 text-[var(--pam-gold)]">
            <Sparkles className="w-4 h-4" />
            <span className="overline">Curated by us</span>
          </div>
          <h3 className="mt-3 font-serif text-3xl sm:text-4xl text-[var(--pam-forest)]">
            The best of the Ghats, hand-picked.
          </h3>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {orderedCurated.map((c, i) => (
              <CuratedListCard key={c.title} c={c} i={i} />
            ))}
          </div>
        </div>
      </div>

      <Lightbox image={lightbox} onClose={() => setLightbox(null)} />
    </section>
  );
}
